import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { SUB_TYPES, subTypeLabel, STATUS_META, STATUS_ORDER, SEVERITY } from "@/lib/constants";
import { downloadCSV } from "@/lib/export";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, AlertTriangle, Loader2, Trash2, Sparkles, MapPin, Download, Search } from "lucide-react";

const inputCls = "w-full bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-[#275C3F] focus:ring-offset-1 outline-none";

function StatusBadge({ status }) {
  const { t } = useLang();
  const m = STATUS_META[status];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-sm" style={{ color: m.color, background: `${m.color}1A` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} /> {t(`status.${status}`)}
    </span>
  );
}

export default function Incidents() {
  const { t } = useLang();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", sub_type: "petroleum_spill", severity: "medium", site: "", location: "" });
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = () => api.get("/incidents").then((r) => { setIncidents(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error("Title and description are required."); return; }
    setCreating(true);
    try {
      await api.post("/incidents", form);
      toast.success("Incident logged & AI-tagged.");
      setOpen(false);
      setForm({ title: "", description: "", sub_type: "petroleum_spill", severity: "medium", site: "", location: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/incidents/${id}/status`, { status });
      setIncidents((prev) => prev.map((i) => (i.id === id ? data : i)));
      if (selected?.id === id) setSelected(data);
    } catch (e) { toast.error("Could not update status."); }
  };

  const remove = async (id) => {
    await api.delete(`/incidents/${id}`);
    setIncidents((prev) => prev.filter((i) => i.id !== id));
    setSelected(null);
    toast.success("Incident deleted.");
  };

  const exportCsv = () => {
    downloadCSV("incidents.csv", [
      { label: "Title", key: "title" },
      { label: "Type", get: (r) => subTypeLabel(r.sub_type) },
      { label: "Severity", key: "severity" },
      { label: "Status", get: (r) => STATUS_META[r.status]?.label || r.status },
      { label: "Site", key: "site" },
      { label: "Location", key: "location" },
      { label: "Citations", get: (r) => (r.citations || []).join(" | ") },
      { label: "Created", key: "created_at" },
    ], filtered);
  };

  const q = query.trim().toLowerCase();
  const filtered = incidents.filter((i) => {
    if (statusFilter !== "all" && i.status !== statusFilter) return false;
    if (typeFilter !== "all" && i.sub_type !== typeFilter) return false;
    if (q && !`${i.title} ${i.description} ${i.site || ""} ${i.location || ""}`.toLowerCase().includes(q)) return false;
    return true;
  });
  const selectCls = "bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-[#275C3F]";

  return (
    <div data-testid="incidents-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><AlertTriangle className="h-7 w-7 text-[#B84A22]" /> {t("inc.title")}</h1>
          <p className="text-[#4B5950] mt-1">{t("inc.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-incidents-button" onClick={exportCsv} disabled={!incidents.length} className="inline-flex items-center gap-2 border border-[#DCE3DD] bg-white px-3 py-2.5 rounded-sm text-sm font-medium hover:bg-[#EAF0EA] disabled:opacity-50">
            <Download className="h-4 w-4" /> {t("common.export")}
          </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-incident-button" className="inline-flex items-center gap-2 bg-[#275C3F] hover:bg-[#1E4731] text-white px-4 py-2.5 rounded-sm text-sm font-semibold transition-all duration-200 active:scale-[0.98]">
              <Plus className="h-4 w-4" /> {t("inc.new")}
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-display">{t("inc.reportTitle")}</DialogTitle>
              <DialogDescription>{t("inc.reportDesc")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto es-scroll pr-1">
              <div>
                <label className="text-sm font-medium">{t("fields.title")}</label>
                <input data-testid="incident-title-input" className={inputCls} value={form.title} onChange={set("title")} placeholder="e.g. Diesel spill at loading bay" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">{t("fields.type")}</label>
                  <select data-testid="incident-subtype-select" className={inputCls} value={form.sub_type} onChange={set("sub_type")}>
                    {SUB_TYPES.map((s) => <option key={s.value} value={s.value}>{t(`subtype.${s.value}`)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">{t("fields.severity")}</label>
                  <select data-testid="incident-severity-select" className={inputCls} value={form.severity} onChange={set("severity")}>
                    {SEVERITY.map((s) => <option key={s} value={s}>{t(`severity.${s}`)}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">{t("fields.site")}</label>
                  <input data-testid="incident-site-input" className={inputCls} value={form.site} onChange={set("site")} placeholder="Plant / facility" />
                </div>
                <div>
                  <label className="text-sm font-medium">{t("fields.location")}</label>
                  <input data-testid="incident-location-input" className={inputCls} value={form.location} onChange={set("location")} placeholder="Area / coordinates" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">{t("fields.description")}</label>
                <textarea data-testid="incident-description-input" rows={4} className={inputCls} value={form.description} onChange={set("description")} placeholder="…" />
              </div>
            </div>
            <DialogFooter>
              <button data-testid="submit-incident-button" onClick={create} disabled={creating} className="inline-flex items-center gap-2 bg-[#275C3F] hover:bg-[#1E4731] text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-60">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {creating ? t("inc.loggingTagging") : t("inc.logIncident")}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#275C3F]" /></div>
      ) : incidents.length === 0 ? (
        <div className="bg-white border border-dashed border-[#DCE3DD] rounded-sm py-16 text-center">
          <AlertTriangle className="h-10 w-10 text-[#DCE3DD] mx-auto" />
          <p className="text-[#4B5950] mt-3">{t("inc.noIncidents")}</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-4 w-4 text-[#4B5950] absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="incident-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("inc.searchPlaceholder")}
                className="w-full bg-white border border-[#DCE3DD] rounded-sm pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#275C3F]" />
            </div>
            <select data-testid="incident-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectCls}>
              <option value="all">{t("inc.allStatuses")}</option>
              {STATUS_ORDER.map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
            </select>
            <select data-testid="incident-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selectCls}>
              <option value="all">{t("inc.allTypes")}</option>
              {SUB_TYPES.map((s) => <option key={s.value} value={s.value}>{t(`subtype.${s.value}`)}</option>)}
            </select>
          </div>
          {filtered.length === 0 ? (
            <div data-testid="incident-no-match" className="bg-white border border-dashed border-[#DCE3DD] rounded-sm py-14 text-center">
              <Search className="h-8 w-8 text-[#DCE3DD] mx-auto" />
              <p className="text-[#4B5950] mt-3">{t("inc.noMatch")}</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filtered.map((inc) => (
            <div key={inc.id} data-testid={`incident-row-${inc.id}`} className="bg-white border border-[#DCE3DD] rounded-sm p-4 hover:border-[#275C3F]/40 transition-all duration-200">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-[#121A14]">{inc.title}</h3>
                    <StatusBadge status={inc.status} />
                    <span className="text-xs bg-[#EAF0EA] text-[#275C3F] px-2 py-0.5 rounded-sm">{subTypeLabel(inc.sub_type, t)}</span>
                    {inc.auto_generated && <span className="text-xs bg-[#D98014]/15 text-[#D98014] px-2 py-0.5 rounded-sm font-semibold">{t("dash.auto")}</span>}
                  </div>
                  <p className="text-sm text-[#4B5950] mt-1 line-clamp-2">{inc.description}</p>
                  {inc.site && <p className="text-xs text-[#4B5950] mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> {inc.site}{inc.location ? ` · ${inc.location}` : ""}</p>}
                  {inc.citations?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {inc.citations.map((c, i) => <span key={i} className="text-[11px] border border-[#2E7599]/40 text-[#2E7599] px-2 py-0.5 rounded-sm">{c}</span>)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select data-testid={`incident-status-select-${inc.id}`} value={inc.status} onChange={(e) => updateStatus(inc.id, e.target.value)} className="bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-2 py-1.5 text-xs">
                    {STATUS_ORDER.map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
                  </select>
                  <button data-testid={`view-incident-${inc.id}`} onClick={() => setSelected(inc)} className="text-xs font-medium text-[#275C3F] border border-[#DCE3DD] px-2.5 py-1.5 rounded-sm hover:bg-[#EAF0EA]">{t("common.details")}</button>
                </div>
              </div>
            </div>
          ))}
            </div>
          )}
        </>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-lg">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-display flex items-center gap-2">{selected.title} <StatusBadge status={selected.status} /></DialogTitle>
                <DialogDescription>{t("inc.detailsDesc")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 max-h-[65vh] overflow-y-auto es-scroll pr-1 text-sm">
                <p className="text-[#4B5950]">{selected.description}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-xs uppercase tracking-wider text-[#4B5950]">{t("fields.type")}</span><p className="font-medium">{subTypeLabel(selected.sub_type, t)}</p></div>
                  <div><span className="text-xs uppercase tracking-wider text-[#4B5950]">{t("fields.severity")}</span><p className="font-medium">{t(`severity.${selected.severity}`)}</p></div>
                </div>
                {selected.ai_summary && (
                  <div className="bg-[#EAF0EA] border border-[#DCE3DD] rounded-sm p-3">
                    <p className="text-xs uppercase tracking-wider text-[#275C3F] font-semibold flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> {t("inc.aiSummary")}</p>
                    <p className="mt-1 text-[#121A14]">{selected.ai_summary}</p>
                  </div>
                )}
                {selected.citations?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#4B5950] font-semibold mb-1">{t("inc.citations")}</p>
                    <div className="flex flex-wrap gap-1.5">{selected.citations.map((c, i) => <span key={i} className="text-xs border border-[#2E7599]/40 text-[#2E7599] px-2 py-0.5 rounded-sm">{c}</span>)}</div>
                  </div>
                )}
                {selected.corrective_actions?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#4B5950] font-semibold mb-1">{t("inc.recommendedActions")}</p>
                    <ul className="list-disc pl-5 space-y-1 text-[#121A14]">{selected.corrective_actions.map((a, i) => <li key={i}>{a}</li>)}</ul>
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#4B5950] font-semibold mb-2">{t("inc.lifecycle")}</p>
                  <div className="space-y-1.5">
                    {selected.history?.map((h, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <StatusBadge status={h.status} />
                        <span className="text-[#4B5950]">{new Date(h.at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <button data-testid={`delete-incident-${selected.id}`} onClick={() => remove(selected.id)} className="inline-flex items-center gap-1.5 text-sm text-[#B84A22] hover:bg-[#B84A22]/10 px-3 py-2 rounded-sm">
                  <Trash2 className="h-4 w-4" /> {t("common.delete")}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
